package com.havn.app.features.analytics.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.analytics.domain.AnalyticsAiAgentPerformance
import com.havn.app.features.analytics.domain.AnalyticsAiConversionFunnel
import com.havn.app.features.analytics.domain.AnalyticsAiInput
import com.havn.app.features.analytics.domain.AnalyticsAiPropertyPerformance
import com.havn.app.features.analytics.domain.AnalyticsAiRecentTrends
import com.havn.app.features.analytics.domain.AnalyticsAiService
import com.havn.app.features.analytics.domain.AnalyticsAiSummaryMetrics
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlin.time.Clock

class SupabaseAnalyticsAiService(
    private val supabaseClient: SupabaseClient,
    private val httpClient: HttpClient = analyticsAiHttpClient(),
    private val json: Json = Json { ignoreUnknownKeys = true; prettyPrint = true }
) : AnalyticsAiService {
    override suspend fun generateInsights(input: AnalyticsAiInput): Result<String> {
        return runCatching {
            val requestId = analyticsAiRequestId()
            val startedAtMs = Clock.System.now().toEpochMilliseconds()
            val accessToken = supabaseClient.auth.currentAccessTokenOrNull()
                ?: error("Sign in again before generating analytics AI insights.")
            val requestBody = json.encodeToString(AnalyticsAiRequest.serializer(), input.toRequest())
            HavnLogger.info(
                "AnalyticsAI",
                "[$requestId] http start language=${input.language} properties=${input.propertyPerformance.size} agents=${input.agentPerformance.size} visitors=${input.summaryMetrics.totalVisitors} bodyChars=${requestBody.length}"
            )
            val httpResponse = try {
                httpClient.post("${HavnSupabaseConfig.url}/functions/v1/generate-analytics-insights") {
                    contentType(ContentType.Application.Json)
                    header(HttpHeaders.Authorization, "Bearer $accessToken")
                    header("apikey", HavnSupabaseConfig.anonKey)
                    setBody(requestBody)
                }
            } catch (throwable: Throwable) {
                val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
                HavnLogger.error("AnalyticsAI", "[$requestId] http exception elapsed=${elapsedMs}ms", throwable)
                if (throwable.isNetworkTimeout()) {
                    throw IllegalStateException("Analytics AI is taking longer than expected. Please try again in a moment.", throwable)
                }
                throw throwable
            }
            val response = httpResponse.body<String>()
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
            HavnLogger.info(
                "AnalyticsAI",
                "[$requestId] http response status=${httpResponse.status.value} elapsed=${elapsedMs}ms responseChars=${response.length}"
            )
            if (!httpResponse.status.isSuccess()) {
                val error = runCatching {
                    json.decodeFromString(AnalyticsAiErrorResponse.serializer(), response).error
                }.getOrNull()
                HavnLogger.warning("AnalyticsAI", "[$requestId] non-success status=${httpResponse.status.value} error=${error ?: response.take(180)}")
                error(error ?: "Unable to generate analytics AI insights right now.")
            }
            val payload = json.decodeFromString<JsonObject>(response)
            HavnLogger.info("AnalyticsAI", "[$requestId] parsed keys=${payload.keys.joinToString(",")}")
            if (!payload.hasAnalyticsInsightKeys()) {
                HavnLogger.warning(
                    "AnalyticsAI",
                    "[$requestId] malformed payload missing insight keys keys=${payload.keys.joinToString(",")}"
                )
                error("AI returned analytics data instead of insights. Please try again in a moment.")
            }
            json.encodeToString(JsonObject.serializer(), payload)
        }.onFailure {
            HavnLogger.error("AnalyticsAI", "Analytics AI insights generation failed", it)
        }
    }
}

private fun analyticsAiHttpClient(): HttpClient = HttpClient {
    install(HttpTimeout) {
        connectTimeoutMillis = 30_000
        socketTimeoutMillis = 120_000
        requestTimeoutMillis = 120_000
    }
}

private fun analyticsAiRequestId(): String =
    Clock.System.now().toEpochMilliseconds().toString()

private fun Throwable.isNetworkTimeout(): Boolean {
    val className = this::class.simpleName.orEmpty()
    val timeoutHere = className.contains("Timeout", ignoreCase = true) ||
        message.orEmpty().contains("timeout", ignoreCase = true)
    return timeoutHere || cause?.isNetworkTimeout() == true
}

private val analyticsInsightKeys = setOf(
    "executive_summary",
    "agency_health_score",
    "key_wins",
    "risks",
    "top_properties",
    "underperforming_properties",
    "agent_coaching_insights",
    "recommended_actions"
)

private fun JsonObject.hasAnalyticsInsightKeys(): Boolean =
    keys.any { it in analyticsInsightKeys }

@Serializable
private data class AnalyticsAiRequest(
    val language: String,
    val agency: AgencyPayload,
    @SerialName("summary_metrics") val summaryMetrics: SummaryMetricsPayload,
    @SerialName("conversion_funnel") val conversionFunnel: ConversionFunnelPayload,
    @SerialName("property_performance") val propertyPerformance: List<PropertyPerformancePayload>,
    @SerialName("agent_performance") val agentPerformance: List<AgentPerformancePayload>,
    @SerialName("recent_trends") val recentTrends: RecentTrendsPayload,
    @SerialName("common_agent_notes") val commonAgentNotes: List<String>,
    @SerialName("output_required") val outputRequired: JsonElement
)

@Serializable
private data class AgencyPayload(
    val name: String,
    @SerialName("report_period") val reportPeriod: ReportPeriodPayload
)

@Serializable
private data class ReportPeriodPayload(val start: String, val end: String)

@Serializable
private data class SummaryMetricsPayload(
    @SerialName("active_properties") val activeProperties: Int,
    @SerialName("open_houses_hosted") val openHousesHosted: Int,
    @SerialName("total_visitors") val totalVisitors: Int,
    @SerialName("total_leads") val totalLeads: Int,
    val inquiries: Int,
    @SerialName("general_inquiries") val generalInquiries: Int,
    @SerialName("property_inquiries") val propertyInquiries: Int,
    @SerialName("lead_sources") val leadSources: Map<String, Int>,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("warm_leads") val warmLeads: Int,
    @SerialName("cold_leads") val coldLeads: Int,
    @SerialName("private_tours_requested") val privateToursRequested: Int,
    @SerialName("followups_sent") val followUpsSent: Int,
    @SerialName("seller_reports_generated") val sellerReportsGenerated: Int,
    @SerialName("offers_recorded") val offersRecorded: Int
)

@Serializable
private data class ConversionFunnelPayload(
    val visitors: Int,
    val leads: Int,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("private_tours") val privateTours: Int,
    val offers: Int
)

@Serializable
private data class PropertyPerformancePayload(
    @SerialName("property_id") val propertyId: String,
    val address: String,
    @SerialName("open_houses") val openHouses: Int,
    val visitors: Int,
    val leads: Int,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("private_tours") val privateTours: Int,
    @SerialName("seller_confidence_score") val sellerConfidenceScore: Int
)

@Serializable
private data class AgentPerformancePayload(
    @SerialName("agent_id") val agentId: String,
    val name: String,
    @SerialName("open_houses_hosted") val openHousesHosted: Int,
    val visitors: Int,
    @SerialName("hot_leads") val hotLeads: Int,
    @SerialName("followups_sent") val followUpsSent: Int,
    @SerialName("average_followup_time_hours") val averageFollowupTimeHours: Double? = null,
    @SerialName("private_tours_booked") val privateToursBooked: Int
)

@Serializable
private data class RecentTrendsPayload(
    @SerialName("visitor_change_vs_previous_period_percent") val visitorChangeVsPreviousPeriodPercent: Int? = null,
    @SerialName("hot_lead_change_vs_previous_period_percent") val hotLeadChangeVsPreviousPeriodPercent: Int? = null,
    @SerialName("private_tour_change_vs_previous_period_percent") val privateTourChangeVsPreviousPeriodPercent: Int? = null,
    @SerialName("followup_completion_change_percent") val followupCompletionChangePercent: Int? = null
)

@Serializable
private data class AnalyticsAiErrorResponse(val error: String)

private fun AnalyticsAiInput.toRequest(): AnalyticsAiRequest = AnalyticsAiRequest(
    language = language,
    agency = AgencyPayload(
        name = agencyName,
        reportPeriod = ReportPeriodPayload(reportPeriodStart, reportPeriodEnd)
    ),
    summaryMetrics = summaryMetrics.toPayload(),
    conversionFunnel = conversionFunnel.toPayload(),
    propertyPerformance = propertyPerformance.map { it.toPayload() },
    agentPerformance = agentPerformance.map { it.toPayload() },
    recentTrends = recentTrends.toPayload(),
    commonAgentNotes = commonAgentNotes,
    outputRequired = requiredOutputJson()
)

private fun AnalyticsAiSummaryMetrics.toPayload(): SummaryMetricsPayload = SummaryMetricsPayload(
    activeProperties = activeProperties,
    openHousesHosted = openHousesHosted,
    totalVisitors = totalVisitors,
    totalLeads = totalLeads,
    inquiries = inquiries,
    generalInquiries = generalInquiries,
    propertyInquiries = propertyInquiries,
    leadSources = leadSources,
    hotLeads = hotLeads,
    warmLeads = warmLeads,
    coldLeads = coldLeads,
    privateToursRequested = privateToursRequested,
    followUpsSent = followUpsSent,
    sellerReportsGenerated = sellerReportsGenerated,
    offersRecorded = offersRecorded
)

private fun AnalyticsAiConversionFunnel.toPayload(): ConversionFunnelPayload = ConversionFunnelPayload(
    visitors = visitors,
    leads = leads,
    hotLeads = hotLeads,
    privateTours = privateTours,
    offers = offers
)

private fun AnalyticsAiPropertyPerformance.toPayload(): PropertyPerformancePayload = PropertyPerformancePayload(
    propertyId = propertyId,
    address = address,
    openHouses = openHouses,
    visitors = visitors,
    leads = leads,
    hotLeads = hotLeads,
    privateTours = privateTours,
    sellerConfidenceScore = sellerConfidenceScore
)

private fun AnalyticsAiAgentPerformance.toPayload(): AgentPerformancePayload = AgentPerformancePayload(
    agentId = agentId,
    name = name,
    openHousesHosted = openHousesHosted,
    visitors = visitors,
    hotLeads = hotLeads,
    followUpsSent = followUpsSent,
    averageFollowupTimeHours = averageFollowupTimeHours,
    privateToursBooked = privateToursBooked
)

private fun AnalyticsAiRecentTrends.toPayload(): RecentTrendsPayload = RecentTrendsPayload(
    visitorChangeVsPreviousPeriodPercent = visitorChangeVsPreviousPeriodPercent,
    hotLeadChangeVsPreviousPeriodPercent = hotLeadChangeVsPreviousPeriodPercent,
    privateTourChangeVsPreviousPeriodPercent = privateTourChangeVsPreviousPeriodPercent,
    followupCompletionChangePercent = followupCompletionChangePercent
)

private fun requiredOutputJson(): JsonObject = buildJsonObject {
    listOf(
        "executive_summary",
        "agency_health_score",
        "key_wins",
        "risks",
        "top_properties",
        "underperforming_properties",
        "agent_coaching_insights",
        "recommended_actions"
    ).forEach { key -> put(key, JsonPrimitive(true)) }
}
